/* eslint-disable max-len */

import * as React from 'react';
import { StylesProps } from '../../../../theme/jss-types';
import { FormField } from '../../create-consignment.types';
import { formFields } from '../../create-modal.constants';
import { scheduleStepStyles } from '../../single-consignment.styles';
import {
    Form,
    FormInstance,
    Collapse,
    Radio,
    Space,
    Button,
    Empty,
} from 'antd';
import {
    CheckCircleFilled,
    HistoryOutlined,
    WarningFilled,
    BulbOutlined,
    PlusCircleOutlined,
    MinusCircleOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { ErrorList, Service } from '../../../../library/Types';
import Loader from '../../../common/Loader';
import { getBusinessTat, getInternationalBusinessTatV2, getServiceabilityRateTat } from '../../../../network/common.api';
import { useTranslation } from 'react-i18next';
import Helper from 'library/Helper';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { ReduxStore } from 'reducers/redux.types';
import { CustomerPortalConfig } from 'types/master-data-types';
import { convertToAPIBodySaveSingle } from '../single-consignment-helper';
import { formRules } from 'library/constants';
import parcelSvg from '../../../../assets/delivery-parcel.svg';

const { Panel } = Collapse;

const {
    useState,
    useEffect,
    useCallback,
} = React;
interface IProps extends StylesProps<ReturnType<typeof scheduleStepStyles>> {
    form: FormInstance;
    params: any;
    showRiskSurchargeType: boolean;
    enableServiceability: boolean;
    allowedConsignmentFormType: any;
    customFieldsList: any;
}

interface PanelRefsMap {
    [key: string]: React.RefObject<HTMLDivElement>;
}

const ServiceTypeSelectorFieldErrorVisibility = (props: IProps) => {
    const {
        classes,
        form,
        params,
        enableServiceability,
        customFieldsList,
        showRiskSurchargeType,
        allowedConsignmentFormType,
    } = props;
    const {
        Services,
    } = formFields;
    const { t, i18n } = useTranslation();
    const [selectedService, setSelectedService] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [services, setServices] = React.useState<any[]>([]);
    const isEditingCN = Form.useWatch('isEditingCN', form);
    const { isCurrentTab, getValidationRules } = params;
    const [activeUAVKeys, setActiveUAVKeys] = useState<string[]>([]);
    const [activeAVKeys, setActiveAVKeys] = useState<string[]>([]);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [showBottomShadow, setShowBottomShadow] = useState(false);
    const [showTopShadow, setShowTopShadow] = useState(false);

    const [panelRefs, setPanelRefs] = useState<PanelRefsMap>({});
    const availablePanelRef = React.useRef<HTMLDivElement>(null);
    const unavailablePanelRef = React.useRef<HTMLDivElement>(null);

    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    const handleAlternateOptionsToggle = (key : string, type : string) => {
        if (type === 'UAV') {
            const newActiveKeys = activeUAVKeys.includes(key)
                ? activeUAVKeys.filter((k) => k !== key)
                : [...activeUAVKeys, key];

            setActiveUAVKeys(newActiveKeys);

            if (!activeUAVKeys.includes(key)) {
                setTimeout(() => {
                    if (panelRefs[key] && panelRefs[key].current) {
                        panelRefs[key].current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                    }
                }, 300);
            }
        } else {
            const newActiveKeys = activeAVKeys.includes(key)
                ? activeAVKeys.filter((k) => k !== key)
                : [...activeAVKeys, key];

            setActiveAVKeys(newActiveKeys);

            if (!activeAVKeys.includes(key)) {
                setTimeout(() => {
                    if (panelRefs[key] && panelRefs[key].current) {
                        panelRefs[key].current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                        });
                    }
                }, 300);
            }
        }
    };

    useEffect(() => {
        if (services.length > 0) {
            const refs: PanelRefsMap = {};
            services.forEach((item : Service) => {
                refs[item.serviceTypeId] = React.createRef<HTMLDivElement>();
            });
            setPanelRefs(refs);
        }
    }, [services]);

    const loadServices = async () => {
        setLoading(true);
        const storage = window.localStorage;
        const formData = form.getFieldsValue();

        const commonParts = {
            srcPincode: formData.srcAddress?.pincode,
            srcStateName: formData.srcAddress?.stateName,
            dstStateName: formData.dstAddress?.stateName,
            dstPincode: formData.dstAddress?.pincode,
            countryName: formData.dstAddress?.countryName,
            clientCode: storage.getItem('userCode'),
            width: formData.width,
            hight: formData.hight,
            length: formData.length,
            weight: formData.weight || 1,
            numPieces: formData?.numberOfPieces,
            referenceNumber: formData.referenceNumber,
            courierType: formData.courierType?.toUpperCase(),
            is_international: formData.is_international,
            pieceDetails: formData?.courierType === 'non-document'
                ? [{
                    length: Helper.converttoCentimeter(formData?.length, formData?.unit),
                    width: Helper.converttoCentimeter(formData?.width, formData?.unit),
                    height: Helper.converttoCentimeter(formData?.height, formData?.unit),
                }] : undefined,
        };
        if (formData.consignmentCategory === 'international') {
            let commodityCode;
            formData?.contentList?.forEach((commodity: any) => {
                if (formData?.commodityId && formData?.commodityId[0] === commodity?.COMMODITY_ID) {
                    commodityCode = commodity?.COMMODITY_CODE;
                }
            });
            const response = await getInternationalBusinessTatV2({
                commodityCode,
                dstCountry: formData?.dstAddress?.countryName,
                dstCity: formData?.dstAddress?.cityName,
                srcCity: formData?.srcAddress?.cityName,
                ...commonParts,
            });
            setServices(response?.data?.map((service: any) => {
                return {
                    ...service,
                    serviceType: service.serviceType,
                    serviceTypeId: service.serviceType,
                };
            }) || []);
            const servicesData = response?.data || [];
            const defaultActiveKey = servicesData.filter((item: Service) => item?.is_serviceable).length === 0 ? ['1', '2'] : ['1'];
            setActiveKeys(defaultActiveKey);
        } else {
            let response:any;
            if (enableServiceability) {
                response = await getServiceabilityRateTat(convertToAPIBodySaveSingle({
                    ...formData,
                }, {
                    showRiskSurchargeType,
                    sendCustomFieldObject: true,
                    customFieldsList,
                    source: 'CUSTOMER_PORTAL_SINGLE_V2',
                }));
            } else {
                response = await getBusinessTat(commonParts);
            }
            setServices(response?.data || []);
            const servicesData = response?.data || [];
            const defaultActiveKey = servicesData.filter((item: Service) => item?.is_serviceable).length === 0 ? ['1', '2'] : ['1'];
            setActiveKeys(defaultActiveKey);
        }
        setLoading(false);
    };

    const prefillData = () => {
        setSelectedService(form.getFieldValue(Services.key));
    };

    useEffect(() => {
        if (isCurrentTab) {
            loadServices();
            prefillData();
        }
    }, [isCurrentTab]);

    const handleSelection = (item: Service) => {
        if (selectedService === item.serviceTypeId) {
            setSelectedService(undefined);
            form.setFieldsValue({
                [Services.key]: undefined,
            });
        } else {
            setSelectedService(item.serviceTypeId);
            form.setFieldsValue({
                [Services.key]: item.serviceTypeId,
            });
        }
    };

    const checkScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (container) {
            setShowBottomShadow(
                container.scrollHeight > container.clientHeight
                    && container.scrollTop + container.clientHeight < container.scrollHeight,
            );

            setShowTopShadow(container.scrollTop > 0);
        }
    }, []);

    useEffect(() => {
        if (services.length > 0) {
            setTimeout(() => {
                checkScroll();
            }, 100);
        }
    }, [services, checkScroll]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            return () => {
                container.removeEventListener('scroll', checkScroll);
                window.addEventListener('resize', checkScroll);
            };
        }
        return undefined;
    }, [checkScroll]);


    const renderTat = (item: Service) => {
        const showTat = allowedConsignmentFormType.includes('TAT');
        const tat = item?.period || item?.tat || item?.TAT;
        if (!tat || !showTat) {
            return <div className={classes.tatNew} />;
        }
        return (
            <div className={classes.columnNew}>
                <img alt="parcel" style={{ height: 18, width: 18 }} src={parcelSvg} />
                <div className={classes.tatNew}>
                    {tat.toString().endsWith('Days') ? tat : `${tat} ${t('days')}`}
                </div>
            </div>
        );
    };

    const renderRate = (item: Service) => {
        if (!item.rate?.amount) {
            return null;
        }
        return (
            <div className={classes.finalPriceNew}>
                {item.rate?.currencySymbol ? `${item.rate.currencySymbol} ` : ''}
                {`${item.rate?.amount}`}
            </div>
        );
    };

    const renderDeliveryTime = (item: Service) => {
        const showDeliveryTime = allowedConsignmentFormType.includes('Delivery Time');
        if (!item.deliveryTime || !showDeliveryTime) {
            return (
                <div className={classes.deliveryTimeNewNA}>
                    {t('not_available')}
                </div>
            );
        }
        return (
            <div className={classes.columnNew}>
                <HistoryOutlined />
                <div className={classes.deliveryTimeNew}>
                    {`${t('delivery_by')} ${item?.deliveryTime}`}
                </div>
            </div>
        );
    };

    const renderAvailableServicePanelHeader = (item: Service) => {
        return (
            <div style={{ display: 'flex', marginLeft: 20 }} onClick={() => handleSelection(item)}>
                {renderTat(item)}
                {renderDeliveryTime(item)}
            </div>
        );
    };

    const renderAvailableServiceHeader = (item: Service) => {
        return (
            <div className={classes.radioGroup}>
                <Radio
                    value={item.serviceTypeId}
                    key={item.serviceTypeId}
                    checked={item.serviceTypeId === selectedService}
                    onClick={() => handleSelection(item)}
                    disabled={isEditingCN}
                >
                    <div className={classes.serviceCard}>
                        <div className={classes.serviceCardLeft}>
                            <div className={classes.serviceItem}>
                                {item.serviceType}
                            </div>
                        </div>
                        <div className={classes.serviceCardRight}>
                            {
                                allowedConsignmentFormType.includes('Rate')
                                    ? <div className={classes.columnNew}>{renderRate(item)}</div>
                                    : null
                            }
                        </div>
                    </div>
                </Radio>
            </div>
        );
    };

    const renderAlternateOptions = (item: Service, type: string) => {
        if (item.error_list?.length === 0) return null;
        const isExpanded = activeUAVKeys.includes(item.serviceTypeId) || activeAVKeys.includes(item.serviceTypeId);

        return (
            <div className={classes.alternateOptions} onClick={() => handleAlternateOptionsToggle(item.serviceTypeId, type)}>
                {type === 'AV' ? t('alternate_options') : t('unavailable')}
                <div
                    style={{
                        display: 'inline-block',
                        transition: 'transform 0.3s ease',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        cursor: 'pointer',
                    }}
                >
                    <RightOutlined />
                </div>
            </div>
        );
    };

    const getSelectedClass = (item: Service) => {
        const classList = [classes.serviceOption];
        if (selectedService === item.serviceTypeId) {
            classList.push(classes.serviceOptionSelected);
        }
        classList.push(classes.serviceOptionAV);
        return classList.join(' ');
    };

    const renderErrorList = (item: Service, type: string) => {
        if (item.error_list?.length === 0) {
            return null;
        }
        return (
            <div className={classes.errorList}>
                <div className={classes.errorHeading}>
                    <BulbOutlined />
                    {type === 'AV' ? `${t('see_why_other_options_for_this_service_are_not_available')} :` : `${t('see_why_unavailable')} :`}
                </div>
                <ul>
                    {item.error_list?.map((itemError: ErrorList) => (
                        <li key={itemError.error_code}>{itemError.error_message}</li>
                    ))}
                </ul>
            </div>
        );
    };

    const formRuleRequired = {
        ...formRules.required,
        message: i18n.exists('required') ? t('required') : 'Required',
    };

    const handleAVCollapseChange = (keys : string | string[]) => {
        const keysArray = Array.isArray(keys) ? keys : [keys];
        const newKey = keysArray.find((key) => !activeAVKeys.includes(key));

        setActiveAVKeys(keysArray);

        if (newKey && panelRefs[newKey] && panelRefs[newKey].current) {
            setTimeout(() => {
                panelRefs[newKey].current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }, 300);
        }
    };

    const renderAvailableService = (item : Service) => {
        const showErrorList = (item.error_list ?? []).length > 0;
        return (
            <div
                className={getSelectedClass(item)}
                ref={panelRefs[item.serviceTypeId]}
            >
                {renderAvailableServiceHeader(item)}
                {showErrorList
                    ? (
                        <Collapse
                            bordered={false}
                            expandIconPosition="end"
                            collapsible="icon"
                            expandIcon={() => renderAlternateOptions(item, 'AV')}
                            onChange={handleAVCollapseChange}
                            activeKey={activeAVKeys}
                        >
                            <Panel
                                header={renderAvailableServicePanelHeader(item)}
                                key={item.serviceTypeId}
                            >
                                {renderErrorList(item, 'AV')}
                            </Panel>
                        </Collapse>
                    ) : (
                        renderAvailableServicePanelHeader(item)
                    )}
            </div>
        );
    };

    const expandAllUnavailableServices = () => {
        const allUAVKeys = services?.filter((item) => (!item.is_serviceable)).map((item) => (item.serviceTypeId));
        setActiveUAVKeys(allUAVKeys);
    };

    const collapseAllUnavailableServices = () => {
        setActiveUAVKeys([]);
    };

    const renderExpandAll = () => {
        return (
            <Button
                className={classes.expandAllHeader}
                onClick={expandAllUnavailableServices}
                type="text"
                disabled={activeUAVKeys?.length === services?.filter((item) => (!item.is_serviceable)).length}
            >
                <PlusCircleOutlined />
                {t('expand_all')}
            </Button>
        );
    };

    const renderCollapseAll = () => {
        return (
            <Button
                className={classes.expandAllHeader}
                onClick={collapseAllUnavailableServices}
                type="text"
                disabled={activeUAVKeys.length === 0}
            >
                <MinusCircleOutlined />
                {t('collapse_all')}
            </Button>
        );
    };

    const handleUAVCollapseChange = (keys : string | string[]) => {
        const keysArray = Array.isArray(keys) ? keys : [keys];
        const newKey = keysArray.find((key) => !activeUAVKeys.includes(key));

        setActiveUAVKeys(keysArray);

        if (newKey && panelRefs[newKey] && panelRefs[newKey].current) {
            setTimeout(() => {
                panelRefs[newKey].current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }, 300);
        }
    };

    const renderUnavailableService = (item : Service) => {
        return (
            <div
                className={classes.serviceOption}
                ref={panelRefs[item.serviceTypeId]}
            >
                <Collapse
                    bordered={false}
                    collapsible="icon"
                    onChange={handleUAVCollapseChange}
                    activeKey={activeUAVKeys}
                    expandIconPosition="end"
                    expandIcon={() => renderAlternateOptions(item, 'NAV')}
                >
                    <Panel
                        header={item.serviceType}
                        key={item.serviceTypeId}
                    >
                        {renderErrorList(item, 'UAV')}
                    </Panel>
                </Collapse>
            </div>
        );
    };

    const renderAvailableServicesHeader = () => {
        return (
            <div className={classes.servicesHeader}>
                <CheckCircleFilled style={{ color: '#27B479' }} />
                {t('available_services')}
            </div>
        );
    };

    const renderUnavailableServicesHeader = () => {
        return (
            <div className={classes.servicesHeader}>
                <WarningFilled style={{ color: 'red' }} />
                {t('unavailable_services')}
            </div>
        );
    };

    const renderAvailableServices = (field: FormField) => {
        return (
            <div style={{ position: 'relative' }}>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '20px',
                        background: 'linear-gradient(to top, rgba(255,255,255,0), rgba(0,0,0,0.05))',
                        pointerEvents: 'none',
                        zIndex: 1,
                        borderRadius: '4px 4px 0 0',
                        opacity: showTopShadow ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                    }}
                />

                <Form.Item
                    name={field.key}
                    initialValue={field.defaultValue}
                    valuePropName={field.valuePropName}
                    style={{ position: 'relative' }}
                    rules={[formRuleRequired, ...getValidationRules(field)]}
                >
                    <div
                        ref={scrollContainerRef}
                        style={{ maxHeight: '360px', overflowY: 'scroll' }}
                        onScroll={checkScroll}
                    >
                        {services?.filter((item) => (item.is_serviceable)).map((item) => renderAvailableService(item))}
                    </div>
                </Form.Item>

                <div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '20px',
                        background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(0,0,0,0.05))',
                        pointerEvents: 'none',
                        zIndex: 1,
                        borderRadius: '0 0 4px 4px',
                        opacity: showBottomShadow ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                    }}
                />
            </div>
        );
    };

    const renderUnavailableServices = () => {
        return (
            <div style={{ maxHeight: '1000px', overflowY: 'scroll' }}>
                {services?.filter((item) => (!item.is_serviceable)).map((item) => renderUnavailableService(item))}
            </div>
        );
    };

    const renderAllOptions = () => {
        return (
            <div className={classes.expandCollapseOptions}>
                {renderCollapseAll()}
                {renderExpandAll()}
            </div>
        );
    };

    const renderServices = () => {
        if (loading) {
            return null;
        }

        const handlePanelChange = (keys: string[]) => {
            setActiveKeys(keys);
            if (keys.includes('2') && !activeKeys.includes('2')) {
                setTimeout(() => {
                    if (unavailablePanelRef.current) {
                        unavailablePanelRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }
                }, 300);
            }
            if (keys.includes('1') && !activeKeys.includes('1')) {
                setTimeout(() => {
                    if (availablePanelRef.current) {
                        availablePanelRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }
                }, 300);
            }
        };

        return (
            <Collapse
                activeKey={activeKeys}
                onChange={handlePanelChange}
                bordered={false}
                expandIconPosition="end"
            >
                <Panel
                    header={renderAvailableServicesHeader()}
                    key="1"
                    className={classes.panel}
                >
                    <div ref={availablePanelRef}>
                        {services?.filter((item) => (item.is_serviceable)).length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Space direction="vertical">
                                {renderAvailableServices(Services)}
                            </Space>
                        )}
                    </div>
                </Panel>
                <Panel
                    header={renderUnavailableServicesHeader()}
                    key="2"
                    className={classes.panel}
                >
                    <div ref={unavailablePanelRef}>
                        {services?.filter((item) => (!item.is_serviceable)).length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Space direction="vertical">
                                {renderAllOptions()}
                                {renderUnavailableServices()}
                            </Space>
                        )}
                    </div>
                </Panel>
            </Collapse>
        );
    };

    if (loading) {
        return (
            <Loader zIndex={10} />
        );
    }

    return (
        <div className={classes.servicesMain}>
            {renderServices()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const customerPortalConfig:CustomerPortalConfig = state?.master?.config?.customer_portal_config;
    const customFieldsList = state?.master?.config?.custom_fields_list_for_form || [];
    const allowedConsignmentFormType = state?.master?.config?.customer_portal_ops_config
        ?.consignment_flow_type_config?.config_value?.allowed_consignment_form_type_customer_portal || [];
    return {
        allowedConsignmentFormType,
        customFieldsList,
        showRiskSurchargeType: customerPortalConfig?.show_risk_surcharge_type,
        enableServiceability: customerPortalConfig?.enable_serviceability,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: scheduleStepStyles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(ServiceTypeSelectorFieldErrorVisibility);
